/**
 * Shared external URL helpers — normalize bare domains for href rendering.
 * Do not use for React Router internal paths (those stay as-is via isExternalHref).
 */

/**
 * Trim and ensure http(s) scheme for external website / social URLs.
 * Leaves internal paths (/...), fragments, and mailto/tel schemes unchanged.
 *
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function normalizeExternalUrl(url) {
  if (!url) return "";

  const trimmed = String(url).trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Preserve internal navigation and special schemes.
  if (/^(\/|#|mailto:|tel:|sms:)/i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

/**
 * Format an external URL for visitor-facing display (strip http/https).
 * Does not change navigation — use normalizeExternalUrl() for hrefs.
 *
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function formatExternalUrlForDisplay(url) {
  if (!url) return "";

  const trimmed = String(url).trim();
  if (!trimmed) return "";

  const mailtoMatch = trimmed.match(/^mailto:(.+)$/i);
  if (mailtoMatch) {
    return mailtoMatch[1].trim();
  }

  const phoneMatch = trimmed.match(/^(?:tel|sms):(.+)$/i);
  if (phoneMatch) {
    return phoneMatch[1].trim();
  }

  return trimmed.replace(/^https?:\/\//i, "");
}

/**
 * Whether a href should open as an external <a> (vs React Router <Link>).
 * Detects http(s) URLs and bare domains (example.com / www.example.com).
 *
 * @param {string|null|undefined} href
 * @returns {boolean}
 */
export function isExternalHref(href) {
  const trimmed = String(href || "").trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/^(\/|#|mailto:|tel:|sms:)/i.test(trimmed)) return false;
  // Bare host: example.com, www.example.com, example.com/path
  return /^(www\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+([/:?#].*)?$/i.test(
    trimmed
  );
}
